<script setup>
import { computed } from 'vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  macro: {
    type: Object,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['delete']);
const { t } = useI18n();

const createdByName = computed(() => {
  const createdBy = props.macro.created_by || {};
  return createdBy.available_name ?? createdBy.email ?? '';
});

const updatedByName = computed(() => {
  const updatedBy = props.macro.updated_by || {};
  return updatedBy.available_name ?? updatedBy.email ?? '';
});

const visibilityLabel = computed(() => {
  const i18nKey =
    props.macro.visibility === 'global'
      ? 'MACROS.EDITOR.VISIBILITY.GLOBAL.LABEL'
      : 'MACROS.EDITOR.VISIBILITY.PERSONAL.LABEL';
  return t(i18nKey);
});
</script>

<template>
  <div
    class="rounded-lg border border-n-gray-2 bg-n-gray-1 dark:border-n-vars-black-bd/80 dark:bg-n-vars-black-br/30 p-3 w-full text-n-gray-3"
  >
    <!-- Top: Name + actions -->
    <div class="flex items-start justify-between gap-2 mb-2">
      <div class="min-w-0">
        <h6 class="block font-semibold text-sm text-n-gray-7 dark:text-n-gray-3 truncate">
          {{ macro.name }}
        </h6>
      </div>
      <div class="flex gap-1 flex-shrink-0">
        <router-link :to="{ name: 'macros_edit', params: { macroId: macro.id } }">
          <woot-button
            v-tooltip.bottom="$t('MACROS.EDIT.TOOLTIP')"
            variant="smooth"
            size="small"
            icon="edit"
            class-names="!bg-n-gray-25 !text-n-gray-5 !border-n-gray-1 dark:!border-n-vars-black-bd/80 dark:!bg-n-vars-black-bd/80 dark:!text-n-gray-3 !rounded-lg !w-8 !h-8 !p-0"
            :is-loading="loading"
          />
        </router-link>
        <woot-button
          v-tooltip.bottom="$t('MACROS.DELETE.TOOLTIP')"
          variant="smooth"
          size="small"
          icon="dismiss-circle"
          class-names="!bg-n-gray-25 !text-n-gray-5 !border-n-gray-1 dark:!border-n-vars-black-bd/80 dark:!bg-n-vars-black-bd/80 dark:!text-n-gray-3 !rounded-lg !w-8 !h-8 !p-0"
          :is-loading="loading"
          @click="$emit('delete', macro)"
        />
      </div>
    </div>

    <!-- Middle: Created by / Last updated by -->
    <div class="flex items-center justify-between mb-2">
      <div class="text-xs text-n-gray-5 truncate">
        {{ $t('MACROS.LIST.TABLE_HEADER.CREATED BY') }}
      </div>
      <div class="flex items-center gap-2 min-w-0">
        <Thumbnail v-if="macro.created_by" :username="createdByName" size="20px" />
        <span class="text-xs font-medium text-n-gray-6 dark:text-n-gray-3 truncate">
          {{ macro.created_by ? createdByName : '--' }}
        </span>
      </div>
    </div>

    <div class="flex items-center justify-between mb-2">
      <div class="text-xs text-n-gray-5 truncate">
        {{ $t('MACROS.LIST.TABLE_HEADER.LAST_UPDATED_BY') }}
      </div>
      <div class="flex items-center gap-2 min-w-0">
        <Thumbnail v-if="macro.updated_by" :username="updatedByName" size="20px" />
        <span class="text-xs font-medium text-n-gray-6 dark:text-n-gray-3 truncate">
          {{ macro.updated_by ? updatedByName : '--' }}
        </span>
      </div>
    </div>

    <!-- Bottom: Visibility -->
    <div class="flex items-center justify-between">
      <div class="text-sm font-medium text-n-gray-5">
        {{ $t('MACROS.LIST.TABLE_HEADER.VISIBILITY') }}
      </div>
      <div class="text-xs font-semibold text-n-gray-6 dark:text-n-gray-3">
        {{ visibilityLabel }}
      </div>
    </div>
  </div>
</template>